<template>
  <div id="credit-returns" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading retailer Credit Returns..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <v-row class="my-2">
          <v-col cols="12" md="6" lg="4" class="py-0">
            <v-alert border="left" dense text outlined type="info">
              <strong
                >Variable Weight Products are excluded from Credit
                Returns</strong
              >
            </v-alert>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2" class="py-0">
            <i-instructions instructionKey="credit-returns"></i-instructions>
          </v-col>
        </v-row>

        <i-local-filter
          class="my-6 mt-md-0 mb-md-2"
          :filterPlaceholder="`Search Credit Returns by Reference Number or ${this.isILR ? 'Customer' : 'Retailer'}`"
          @filter="localFilter"
        />

        <template v-if="!loading && filteredCreditReturns.length === 0">
          <i-empty-results
            buttonText="Dashboard"
            buttonRouteName="dashboard"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <v-row no-gutters>
            <v-col cols="12">
              <template v-if="$vuetifyDisplay.smAndDown">
                <div
                  class="mt-2 pa-0"
                  v-for="item in filteredCreditReturns"
                  :key="item.id"
                >
                  <v-card class="rounded-2 pa-4 mb-6">
                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span
                          ><strong>{{ headers[0].text }}</strong></span
                        >
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span
                          ><strong>{{ item.referenceNumber }}</strong></span
                        >
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[1].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(item.createdAt)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[2].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{ item.store.name }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[3].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.currency(
                            displayCreditReturnTotal(item.supplierTotalIncFee)
                          )
                        }}</span>
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <p
                      v-if="item.status === 'OUTSTANDING'"
                      class="g-bold g-border-radius-s i-status-block-orange px-2 py-3 text-center"
                    >
                      <strong>CREDIT RETURN STATUS: OUTSTANDING</strong>
                    </p>

                    <v-divider class="my-2"></v-divider>

                    <v-btn
                      rounded
                      class="i-btn-block-secondary g-full-width"
                      :to="{
                        name: 'credit-return-details',
                        params: { id: item.id }
                      }"
                    >
                      View
                    </v-btn>
                  </v-card>
                </div>
              </template>

              <template v-else>
                <v-data-table
                  class="mt-2 mt-md-4 pt-2 elevation-1 i-data-table"
                  :headers="headers"
                  :loading="loading"
                  :items="filteredCreditReturns"
                  :items-per-page="-1"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                >
                  <template
                    v-slot:[`item.supplierTotalIncFee.amountIncGst`]="{ item }"
                  >
                    <span>{{
                      $filters.currency(
                        displayCreditReturnTotal(item.supplierTotalIncFee)
                      )
                    }}</span>
                  </template>

                  <template v-slot:[`item.createdAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.createdAt)
                    }}</span>
                  </template>

                  <template v-slot:[`item.details`]="{ item }">
                    <v-btn
                      rounded
                      class="i-btn-block-secondary p-btn"
                      :to="{
                        name: 'credit-return-details',
                        params: { id: item.id }
                      }"
                    >
                      <span><strong>View</strong></span>
                    </v-btn>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <p
                      v-if="item.status === 'OUTSTANDING'"
                      class="g-bold g-border-radius-s i-status-block-orange px-2 py-3 my-1 text-center"
                    >
                      OUTSTANDING
                    </p>
                  </template>
                </v-data-table>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { fetchCreditReturns } from '@/services/creditReturns'
import iOverlay from '@/components/Overlay'
import iLocalFilter from '@/components/LocalFilter'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import {
  buildSupplierTotalLabel,
  pickSupplierCostTotal
} from '@/utils/supplierAmountDisplay'

const CREDIT_RETURNS_STATUS = ['OUTSTANDING']

export default {
  components: {
    iEmptyResults,
    iInstructions,
    iOverlay,
    iLocalFilter
  },

  data: () => ({
    loading: false,
    emptyFilteredResultsText: 'No Credit Returns found',
    currentPage: 1,
    pageSize: 1000,
    pagination: {
      total: 0,
      totalPages: 0
    },
    creditReturns: [],
    filterText: ''
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('creditReturns', ['outstandingCreditReturns']),
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),
    headers() {
      return [
        {
          text: 'CREDIT RETURN REFERENCE NUMBER',
          value: 'referenceNumber',
          width: '20%'
        },
        { text: 'CREATED DATE', value: 'createdAt' },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'store.name',
          width: '20%'
        },
        {
          text: buildSupplierTotalLabel(
            this.tenantCountryRegion,
            'TOTAL CREDIT AMOUNT'
          ),
          value: 'supplierTotalIncFee.amountIncGst',
          align: 'center'
        },
        { text: 'DETAILS', value: 'details', align: 'center', sortable: false },
        { text: 'STATUS', value: 'status', align: 'center' }
      ]
    },
    filteredCreditReturns() {
      return this.creditReturns.filter((order) => {
        return (
          order.referenceNumber.includes(this.filterText) ||
          order.store.name.includes(this.filterText)
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('creditReturns', ['updateOutstandingCount']),

    displayCreditReturnTotal(amount) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: amount
      })
    },

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    fetch(currentPage, pageSize, status) {
      this.loading = true

      // currentPage decreased by 1 as currentPage API starts from 0
      currentPage -= 1

      return fetchCreditReturns(
        this.selectedSupplierId,
        currentPage,
        pageSize,
        status
      )
        .then((res) => {
          this.creditReturns = res.creditReturns
          this.pagination = res.pagination
          this.updateOutstandingCount(this.creditReturns.length)
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  mounted() {
    this.fetch(this.currentPage, this.pageSize, CREDIT_RETURNS_STATUS)
  },

  watch: {
    outstandingCreditReturns() {
      this.fetch(this.currentPage, this.pageSize, CREDIT_RETURNS_STATUS)
    }
  }
}
</script>

<style lang="scss" scoped>
@media only screen and (min-width: 960px) {
  #credit-returns {
    .p-btn {
      width: 100px;
      height: 30px;
    }
  }
}

@media only screen and (min-width: 1264px) {
  #credit-returns {
    .p-btn {
      width: 120px;
    }
  }
}
</style>
